export type StateProps = {
  [key: string]: boolean | undefined | [boolean, string];
};

/**
 * Merges class names with state-based class names.
 *
 * @param {string | string[] | null} className - The base class name(s).
 * @param {StateProps} state - The state object containing boolean or [boolean, string] pairs.
 * @returns {string} - The merged class names.
 *
 * @example
 *
 * const classNames = mergeClasses('item__base', {
 *   ['active__class']: true,
 *   ['open__class']: true,
 *   ['disabled__class']: false,
 *   ['hover__class']: undefined,
 * });
 *
 * console.log(classNames);
 * // Output: 'item__base active__class open__class'
 */
export function mergeClasses(
  className?: string | (string | undefined)[] | null,
  state?: StateProps
): string {
  const base =
    typeof className === 'string'
      ? className
      : Array.isArray(className)
        ? className.filter(Boolean).join(' ')
        : '';

  const stateClasses =
    state && typeof state === 'object'
      ? Object.entries(state)
          .filter(([_, value]) => {
            if (Array.isArray(value)) {
              return value[0];
            }
            return !!value;
          })
          .map(([key, value]) => (Array.isArray(value) ? value[1] : key))
          .filter(Boolean)
      : [];

  return [base, ...stateClasses].filter(Boolean).join(' ');
}
